import { ReactNode, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useModal } from "@/contexts/ModalContext";

interface ModalFormProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  children: ReactNode;
  submitText?: string;
  cancelText?: string;
  className?: string;
  submitDisabled?: boolean;
}

export const ModalForm = ({
  title,
  isOpen,
  onClose,
  onSubmit,
  children,
  submitText = "Add",
  cancelText = "Cancel",
  className,
  submitDisabled = false,
}: ModalFormProps) => {
  const { openModal, closeModal } = useModal();

  useEffect(() => {
    if (isOpen) openModal();
    else closeModal();
  }, [isOpen, openModal, closeModal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-3xl max-h-[95vh] flex flex-col bg-white dark:bg-gray-900 rounded-sm shadow-lg overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
            className
          )}
        >
          <div className="flex justify-between items-center p-4 border-b dark:border-gray-700 shrink-0">
            <DialogPrimitive.Title className="text-xl font-semibold dark:text-gray-100">
              {title}
            </DialogPrimitive.Title>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {children}
            </div>

            <div className="flex justify-end p-4 border-t dark:border-gray-700 gap-3 shrink-0 bg-white dark:bg-gray-900">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {cancelText}
              </button>
              <button
                type="submit"
                disabled={submitDisabled}
                className={cn(
                  "px-4 py-2 text-sm font-medium text-white bg-[#5a7ebf] rounded-sm transition-colors",
                  submitDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#5a7ebf]/90"
                )}
              >
                {submitText}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
